/**
 * Dashboard.tsx — redesign V4, PAINEL fiel ao protótipo
 * (design_handoff_erp_redesign/prototype/…/Redesign.html → SCREENS.painel).
 *
 * Reproduz exatamente: header (período seg + Importar eDSP + Novo dossier),
 * 4 KPIs com tile de ícone tonalizado + trend + sparkline, e grid2 com
 * "Dossiers recentes" (tabela densa .tbl + chips) + trilha (Reconciliação
 * bancária / A fazer / Trésorerie).
 *
 * Dados REAIS via tRPC: dossiers.summary, treasury.summary, dossiers.list,
 * bankTransactions.list (contagem de reconciliação). Sparklines são
 * decorativas (como no protótipo). Nº formatados por lib/format-fr.
 */

import {
  AlertTriangle,
  ArrowDownLeft,
  ArrowUpRight,
  Euro,
  FileCheck2,
  FilePen,
  FolderOpen,
  HandCoins,
  Hourglass,
  Landmark,
  Plus,
  ShoppingCart,
  SlidersHorizontal,
  TrendingDown,
  TrendingUp,
  Upload,
  UserPlus,
  Wallet,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { ErpShell } from "../components/layout/ErpShell.js";
import { OperationBoard } from "../components/domain/OperationBoard.js";
import { HomeBriefing } from "../components/domain/HomeBriefing.js";
import { useAuth } from "../hooks/useAuth.js";
import { useCanEdit } from "../hooks/useCanEdit.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { useI18n } from "../i18n/I18nProvider.js";
import { useT } from "../i18n/index.js";
import { formatMoney } from "../lib/format-fr.js";
import { rankPayouts } from "./dashboard-payouts.js";
import type { Locale } from "../i18n/messages.js";
import { trpc } from "../lib/trpc.js";
import { useGrantOnly } from "../hooks/useGrantOnly.js";

const INTL_LOCALE: Record<Locale, string> = {
  fr: "fr-FR",
  pt: "pt-BR",
  en: "en-GB",
  es: "es-ES",
  uk: "uk-UA",
};

type Period = "month" | "year" | "all";
const PERIOD_KEY = "eurostorm:dashboardPeriod";
const pad = (n: number) => String(n).padStart(2, "0");
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function periodRange(p: Period): { from?: string; to?: string } {
  const now = new Date();
  const y = now.getFullYear();
  if (p === "all") return {};
  if (p === "month") {
    const m = now.getMonth();
    return { from: ymd(new Date(y, m, 1)), to: ymd(new Date(y, m + 1, 0)) };
  }
  return { from: ymd(new Date(y, 0, 1)), to: ymd(new Date(y, 11, 31)) };
}

const num = (v: number | string | null | undefined) => Number(v ?? 0);
const money = (v: number | string | null | undefined) => formatMoney(v, { decimals: 0 });

/* Sparklines decorativas (como no protótipo). */
const SPARKS: Record<string, number[]> = {
  accent: [10, 13, 11, 16, 15, 19, 22, 20, 26],
  info: [14, 12, 15, 13, 17, 16, 19, 18, 21],
  success: [8, 10, 9, 12, 11, 14, 13, 17, 16],
  warn: [20, 19, 21, 18, 17, 19, 16, 15, 14],
};
const TONE: Record<string, { bg: string; fg: string }> = {
  accent: { bg: "var(--accent-soft)", fg: "var(--accent)" },
  info: { bg: "var(--info-50)", fg: "var(--info-500)" },
  success: { bg: "var(--success-50)", fg: "var(--success-500)" },
  warn: { bg: "var(--warning-50)", fg: "var(--warning-500)" },
};

interface Kpi {
  lab: string;
  value: string;
  cur?: string;
  icon: LucideIcon;
  tone: keyof typeof TONE;
  trend?: { dir: "up" | "down"; delta: string };
  note: string;
  href: string;
}

/**
 * F-dashboard-tech-payouts — card com os NOMES dos técnicos a pagar.
 * Os KPIs numéricos só dizem "116 dossiers / 173 664 €"; aqui o operador vê
 * de quem se trata sem abrir a página de pagamentos. Ordenado por valor desc.
 * "Liberado" (cliente já pagou) vem em destaque; o resto fica esmaecido.
 */
function TechPayoutsCard({
  data,
  isLoading,
  t,
  urgentTotal,
}: {
  /**
   * Total já RECEBIDO do cliente e ainda não repassado — mesmo número do
   * alerta vermelho na faixa de pendências. Quando > 0 o card inteiro assume
   * o tom de urgência, para o operador não precisar cruzar faixa × card.
   */
  urgentTotal: number;
  data:
    | {
        technicians: {
          technicianId: number;
          technicianName: string;
          releasedTotal: number;
          pendingTotal: number;
          dossierCount: number;
        }[];
      }
    | undefined;
  isLoading: boolean;
  t: ReturnType<typeof useT>;
}) {
  // Urgente = já recebemos do cliente e ainda não repassamos. Mesmo critério
  // (e mesmas cores) do alerta vermelho na faixa de pendências.
  const urgent = urgentTotal > 0;
  // Ordenação/corte em dashboard-payouts.ts — função pura, coberta por teste
  // (a soma dos exibidos TEM que fechar com o total do rodapé).
  const {
    rows,
    othersCount,
    othersTotal,
    metricOf: metric,
  } = rankPayouts(data?.technicians ?? [], { urgent, top: 5 });
  const bg = urgent ? "var(--danger-50)" : TONE.accent!.bg;
  const fg = urgent ? "var(--danger-500)" : TONE.accent!.fg;
  // F-payouts-tech-deeplink (CEO 2026-09-12): clicar num TÉCNICO do card abre
  // a página já filtrada NELE (?tecnico=<id>); clicar no resto do card abre a
  // visão geral. <a> aninhado é inválido → navegação programática.
  const [, navigate] = useLocation();
  return (
    <div
      className="kpi kpi--link"
      role="link"
      tabIndex={0}
      onClick={() => navigate(urgent ? "/pagamentos-tecnicos?liberados=1" : "/pagamentos-tecnicos")}
      style={{ cursor: "pointer", ...(urgent ? { borderColor: "var(--danger-500)" } : {}) }}
    >
      <div className="kpi__top">
        <span className="kpi__lab" style={urgent ? { color: fg } : undefined}>
          {urgent ? "⚠ " : ""}
          {t("dashboard.kpiTechPayouts")}
        </span>
        <span className="kpi__ico" style={{ background: bg, color: fg }}>
          <HandCoins size={17} strokeWidth={1.9} />
        </span>
      </div>
      {isLoading ? (
        <div className="kpi__d" style={{ marginTop: 8 }}>
          …
        </div>
      ) : rows.length === 0 ? (
        <div className="kpi__d" style={{ marginTop: 8 }}>
          {t("dashboard.techPayoutsEmpty")}
        </div>
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 8 }}>
            {rows.map((r) => (
              <div
                key={r.technicianId}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(
                    `/pagamentos-tecnicos?${r.releasedTotal > 0 ? "liberados=1" : "pendentes=1"}&tecnico=${r.technicianId}`,
                  );
                }}
                className="hover:underline"
                style={{ display: "flex", alignItems: "baseline", gap: 8, fontSize: 12.5 }}
                title={
                  r.releasedTotal > 0
                    ? t("dashboard.techPayoutsReleasedHint")
                    : t("dashboard.techPayoutsBlockedHint")
                }
              >
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: "var(--text2)",
                  }}
                >
                  {r.technicianName}
                </span>
                <span
                  className="num"
                  style={{
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    // Liberado (cliente pagou) = pague agora → cor de urgência.
                    // Sem liberação ainda depende do cliente → esmaecido.
                    color: r.releasedTotal > 0 ? "var(--danger-500)" : "var(--text3)",
                  }}
                >
                  {money(metric(r))}
                </span>
              </div>
            ))}
          </div>
          <div className="kpi__d" style={{ marginTop: 8 }}>
            {othersCount > 0
              ? `${t("dashboard.techPayoutsOthersPre")}${othersCount} ${t("dashboard.techPayoutsOthersSuf")} · ${money(othersTotal)}`
              : `${rows.length} ${
                  rows.length === 1
                    ? t("dashboard.techPayoutsTechCountSufSingular")
                    : t("dashboard.techPayoutsTechCountSuf")
                }`}
          </div>
          {urgent && (
            // Chamada de ação: o total que já está em caixa esperando repasse.
            <div
              style={{
                marginTop: 6,
                fontSize: 12,
                fontWeight: 700,
                color: "var(--danger-500)",
              }}
            >
              {money(urgentTotal)} {t("dashboard.techPayoutsPayNow")}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function KpiCards({ kpis, extra }: { kpis: Kpi[]; extra?: ReactNode }) {
  return (
    <div className="kpis">
      {kpis.map((k) => {
        const Icon = k.icon;
        const { bg, fg } = TONE[k.tone]!;
        const Tr = k.trend?.dir === "down" ? TrendingDown : TrendingUp;
        return (
          <Link className="kpi kpi--link" href={k.href} key={k.lab}>
            <div className="kpi__top">
              <span className="kpi__lab">{k.lab}</span>
              <span className="kpi__ico" style={{ background: bg, color: fg }}>
                <Icon size={17} strokeWidth={1.9} />
              </span>
            </div>
            <div className="kpi__v num">
              {k.value}
              {k.cur ? <span className="cur">{k.cur}</span> : null}
            </div>
            <div className="kpi__d">
              {k.trend ? (
                <span className={`trend trend--${k.trend.dir}`}>
                  <Tr size={13} /> {k.trend.delta}
                </span>
              ) : null}{" "}
              {k.note}
            </div>
            <div className="spark">
              {SPARKS[k.tone]!.map((h, i) => (
                <i key={i} style={{ height: h, background: fg }} />
              ))}
            </div>
          </Link>
        );
      })}
      {extra}
    </div>
  );
}

/* ---- Dossiers recentes (linha) ---- */
interface DossierRow {
  id: number;
  customerInvoiceNumber?: string | null;
  invoiceNumberExternal?: string | null;
  dossierCode?: string | null;
  dossierDate?: string | null;
  serviceDate?: string | null;
  createdAt?: string | null;
  vehicleMakeSnapshot?: string | null;
  vehicleModelSnapshot?: string | null;
  technicianName?: string | null;
  customerDisplayName?: string | null;
  endCustomerName?: string | null;
  facturadoTotal?: number | string | null;
  technicianAmountTotal?: number | string | null;
  docStatus?: string | null;
  customerInvoiceStatus?: string | null;
}

const refOf = (d: DossierRow) =>
  d.customerInvoiceNumber ?? d.invoiceNumberExternal ?? d.dossierCode ?? `#${d.id}`;

function dateOf(d: DossierRow, intl: string): string {
  const raw = d.dossierDate ?? d.serviceDate ?? d.createdAt;
  if (!raw) return "—";
  const dt = new Date(raw);
  return isNaN(dt.getTime())
    ? String(raw)
    : dt.toLocaleDateString(intl, { day: "2-digit", month: "2-digit" });
}

function statusBadge(d: DossierRow, t: ReturnType<typeof useT>): { cls: string; txt: string } {
  const s = (d.customerInvoiceStatus ?? d.docStatus ?? "").toLowerCase();
  if (["paid", "paid_full", "faturado", "invoiced", "issued", "sent"].some((x) => s.includes(x)))
    return { cls: "b-ok", txt: t("dashboard.statusInvoiced") };
  if (["pending", "draft", "aberto", "open"].some((x) => s.includes(x)))
    return { cls: "b-info", txt: t("dashboard.statusPending") };
  if (["overdue", "atraso", "late"].some((x) => s.includes(x)))
    return { cls: "b-warn", txt: t("dashboard.statusReview") };
  return { cls: "b-mut", txt: s ? s : "—" };
}

function Painel() {
  const { locale } = useI18n();
  const podeEditar = useCanEdit();
  // F-acesso-por-menu — gerente de chantier: sem cards de caixa/KPIs da empresa.
  const { grantOnly } = useGrantOnly();
  const intl = INTL_LOCALE[locale];
  const [, setLocation] = useLocation();
  const t = useT();
  const companiesQuery = trpc.companies.list.useQuery();
  const companies = useMemo(() => companiesQuery.data ?? [], [companiesQuery.data]);
  const [companyId] = useSelectedCompany(companies);
  const company = companies.find((c) => c.id === companyId);
  const enabled = companyId != null;

  const [period, setPeriod] = useState<Period>(() => {
    const s = typeof window !== "undefined" ? window.localStorage.getItem(PERIOD_KEY) : null;
    return s === "month" || s === "year" || s === "all" ? s : "year";
  });
  const choose = (p: Period) => {
    setPeriod(p);
    try {
      window.localStorage.setItem(PERIOD_KEY, p);
    } catch {
      /* ignore */
    }
  };
  const range = periodRange(period);

  const summaryQ = trpc.dossiers.summary.useQuery(
    { companyId: companyId ?? 0, from: range.from, to: range.to },
    { enabled },
  );
  const treasuryQ = trpc.treasury.summary.useQuery(
    { companyId: companyId ?? 0 },
    { enabled: enabled && !grantOnly },
  );
  // F-fatura-editada (CEO 2026-11-03) — editadas no mesmo número a reenviar.
  const unsentInvQ = trpc.customerInvoices.unsentSummary.useQuery(
    { companyId: companyId ?? 0 },
    { enabled: enabled && !grantOnly },
  );
  // F-dashboard-tech-payouts — nomes dos técnicos a pagar. Traz a lista
  // INTEIRA (são poucos por empresa): o corte "top 5 + resto" é feito no
  // card, que ordena pela métrica do momento (liberado × pendente). Cortar
  // aqui deixaria a soma exibida sem fechar com o total do rodapé.
  const techPayoutsQ = trpc.treasury.technicianPayouts.useQuery(
    { companyId: companyId ?? 0 },
    { enabled: enabled && !grantOnly },
  );
  const dossiersQ = trpc.dossiers.list.useQuery(
    { companyId: companyId ?? 0, limit: 8 },
    { enabled },
  );
  const bankAllQ = trpc.bankTransactions.list.useQuery(
    { companyId: companyId ?? 0, limit: 1 },
    { enabled: enabled && !grantOnly },
  );
  const bankUnrecQ = trpc.bankTransactions.list.useQuery(
    { companyId: companyId ?? 0, reconciliationStatus: "unreconciled", limit: 1 },
    { enabled: enabled && !grantOnly },
  );
  // Contagem de dossiers ABERTOS (fatura do cliente pendente = a faturar) —
  // é o que o card "Dossiers abertos" conta E o que o clique abre (mesmo filtro).
  const openDossiersQ = trpc.dossiers.list.useQuery(
    { companyId: companyId ?? 0, customerInvoiceStatus: "pending", limit: 1 },
    { enabled },
  );
  const openDossiers = openDossiersQ.data?.total ?? 0;

  const s = summaryQ.data;
  const tr = treasuryQ.data;
  const rows = (dossiersQ.data?.rows ?? []) as unknown as DossierRow[];

  const facturado = num(s?.facturadoTotal);
  const aReceber = num(tr?.receivable.customer.openTotal);
  const jaRecebido = Math.max(0, facturado - aReceber);
  // % do faturado só faz sentido quando o faturado é a base (ES). Na BS o
  // faturado é ~0 e o recebível é intercompany → a razão explode (8000%+).
  // Só mostramos o trend quando a razão é sensata (≤ 150%).
  const receberPct = facturado > 0 ? (aReceber / facturado) * 100 : Infinity;
  const recebidoPct = facturado > 0 ? (jaRecebido / facturado) * 100 : 0;
  // F-lucro-bruto — comissões 0 com faturado > 0 = linhas legacy (comissão não
  // computada) → o Lucro Bruto vira ≈ Faturado (superestimado). Sinaliza.
  const comissoesZero = s != null && num(s.comissoesTotal) === 0 && facturado > 0;

  const kpis: Kpi[] = [
    {
      lab: t("dashboard.kpiRevenue"),
      value: money(facturado),
      cur: "€",
      icon: Euro,
      tone: "accent",
      note: s ? `${s.dossierCount} ${t("dashboard.dossiersSuf")}` : "…",
      href: "/faturas",
    },
    {
      lab: t("dashboard.kpiGrossProfit"),
      value: money(Number(s?.lucroBruto ?? 0)),
      cur: "€",
      icon: TrendingUp,
      tone: comissoesZero ? "warn" : "success",
      note: !s
        ? "…"
        : comissoesZero
          ? `⚠ ${t("dashboard.commissionsNotComputed")}`
          : `${t("dashboard.invoicedMinusCommissionsPre")} (${money(Number(s.comissoesTotal ?? 0))} €)`,
      // Leva à lista de dossiers do MESMO período do KPI (mês/exercício).
      href: range.from && range.to ? `/dossiers?from=${range.from}&to=${range.to}` : "/dossiers",
    },
    {
      lab: t("dashboard.kpiReceivable"),
      value: money(aReceber),
      cur: "€",
      icon: Hourglass,
      tone: "info",
      ...(receberPct <= 150
        ? {
            trend: { dir: "up" as const, delta: `${receberPct.toFixed(1)}%` },
            note: t("dashboard.ofInvoiced"),
          }
        : {
            note: tr ? `${tr.receivable.customer.count} ${t("dashboard.openInvoicesSuf")}` : "…",
          }),
      href: "/faturas?status=issued",
    },
    {
      lab: t("dashboard.kpiReceived"),
      value: money(jaRecebido),
      cur: "€",
      icon: Wallet,
      tone: "success",
      ...(jaRecebido > 0 && recebidoPct > 0
        ? {
            trend: { dir: "up" as const, delta: `${recebidoPct.toFixed(1)}%` },
            note: t("dashboard.ofInvoiced"),
          }
        : { note: t("dashboard.inFiscalYear") }),
      href: "/faturas?status=paid",
    },
    {
      lab: t("dashboard.kpiOpenDossiers"),
      value: openDossiersQ.isLoading ? "…" : String(openDossiers),
      icon: FolderOpen,
      tone: "warn",
      note: s ? `${s.dossierCount} ${t("dashboard.inFiscalYear")}` : t("dashboard.toInvoice"),
      href: "/dossiers?status=pending",
    },
  ];

  // Reconciliação (real)
  const bankTotal = bankAllQ.data?.total ?? 0;
  const bankUnrec = bankUnrecQ.data?.total ?? 0;
  const bankRec = Math.max(0, bankTotal - bankUnrec);
  const bankPct = bankTotal > 0 ? Math.round((bankRec / bankTotal) * 100) : 0;

  // F-ops-home — carros prontos a faturar por cliente (padrão eDSP).
  // CEO 2026-10-13 — reparados com o dégarnissage sem registro (alerta).
  const degarnQuery = trpc.dossiers.degarnissagePending.useQuery(
    { companyId: companyId ?? 0 },
    { enabled: companyId != null },
  );
  const degarn = degarnQuery.data;

  // F-tech-signup — candidatura de técnico esperando aprovação. Entra na
  // lista de pendências porque é aqui que o escritório olha; a aba fica
  // dentro de Técnicos e o CEO não a encontrou ao testar.
  const appsQ = trpc.technicianApplications.list.useQuery(
    { status: "pending", limit: 1 },
    { enabled: podeEditar && !grantOnly },
  );
  const candidaturas = appsQ.data?.pendingCount ?? 0;

  // A fazer (real — só itens que existem)
  const todos: { icon: LucideIcon; bg: string; fg: string; t: string; s: string; href: string }[] =
    [];
  const nAmended = unsentInvQ.data?.amendedCount ?? 0;
  if (nAmended > 0)
    todos.push({
      icon: FilePen,
      bg: "var(--warning-50)",
      fg: "var(--warning-500)",
      t: `${nAmended} ${t("dashboard.amendedInvoicesSuf")}`,
      s: t("dashboard.amendedInvoicesSub"),
      href: "/faturas?modificadas=1",
    });
  // Prioridade: técnicos JÁ RECEBIDOS do cliente e ainda não pagos (dinheiro em
  // caixa a repassar). Subconjunto "liberado" do gate — o mais urgente.
  if (tr && num(tr.payable.technicianCommission.releasedTotal) > 0)
    todos.push({
      icon: HandCoins,
      bg: "var(--danger-50)",
      fg: "var(--danger-500)",
      t: `${tr.payable.technicianCommission.releasedDossierCount} ${t("dashboard.dossiersPayoutTechnicianSuf")}`,
      s: `${money(tr.payable.technicianCommission.releasedTotal)} ${t("dashboard.receivedToPaySuf")}`,
      // F-payouts-released-filter — abre a página JÁ filtrada no subconjunto
      // do alerta (pendente × cliente recebido); a URL vence o localStorage.
      href: "/pagamentos-tecnicos?liberados=1",
    });
  if (candidaturas > 0)
    todos.push({
      icon: UserPlus,
      bg: "var(--info-50)",
      fg: "var(--info-500)",
      t: `${candidaturas} ${
        candidaturas === 1
          ? t("dashboard.techApplicationsOneSuf")
          : t("dashboard.techApplicationsSuf")
      }`,
      s: t("dashboard.techApplicationsHint"),
      href: "/tecnicos?aba=candidaturas",
    });
  if (bankUnrec > 0)
    todos.push({
      icon: Landmark,
      bg: "var(--info-50)",
      fg: "var(--info-500)",
      t: `${bankUnrec} ${t("dashboard.unreconciledTxSuf")}`,
      s: t("dashboard.bankStatement"),
      href: "/banco?status=unreconciled&periodo=todos",
    });
  if (tr && num(tr.receivable.customer.overdueTotal) > 0)
    todos.push({
      icon: AlertTriangle,
      bg: "var(--danger-50)",
      fg: "var(--danger-500)",
      t: t("dashboard.overdueInvoices"),
      s: `${money(tr.receivable.customer.overdueTotal)} ${t("dashboard.overdueSuf")}`,
      href: "/faturas?status=issued",
    });
  if (tr && tr.payable.technicianCommission.dossierCount > 0)
    todos.push({
      icon: Wrench,
      bg: "var(--accent-soft)",
      fg: "var(--accent)",
      t: `${tr.payable.technicianCommission.dossierCount} ${t("dashboard.dossiersCommissionsSuf")}`,
      s: `${money(tr.payable.technicianCommission.pendingTotal)} ${t("dashboard.toPaySuf")}`,
      href: "/pagamentos-tecnicos?pendentes=1",
    });
  if (tr && tr.payable.supplier.count > 0)
    todos.push({
      icon: ShoppingCart,
      bg: "var(--warning-50)",
      fg: "var(--warning-500)",
      t: `${tr.payable.supplier.count} ${t("dashboard.supplierInvoicesSuf")}`,
      s: `${money(tr.payable.supplier.openTotal)} ${t("dashboard.toPaySuf")}`,
      href: "/compras",
    });

  return (
    <>
      {/* F-home-briefing (CEO 2026-10-22, fase 1) — a home abre com o que
          precisa de você hoje: substitui a faixa amarela, o card de
          dégarnissage e o "voitures à facturer" do topo. */}
      <HomeBriefing
        companyId={companyId}
        grantOnly={grantOnly}
        intl={intl}
        todos={todos}
        degarn={degarn}
      />
      <div className="ph">
        <div>
          <h1>{t("dashboard.title")}</h1>
          <p>
            {company
              ? `${company.name} · ${t("dashboard.fiscalYearLabel")} ${new Date().getFullYear()}`
              : "…"}
          </p>
        </div>
        <div className="ph__actions">
          <div className="seg">
            <button className={period === "month" ? "on" : ""} onClick={() => choose("month")}>
              {t("dashboard.periodMonth")}
            </button>
            <button className={period === "year" ? "on" : ""} onClick={() => choose("year")}>
              {t("dashboard.periodYear")}
            </button>
            <button className={period === "all" ? "on" : ""} onClick={() => choose("all")}>
              {t("dashboard.period12m")}
            </button>
          </div>
          {!grantOnly && (
            <Link href="/import/edsp" className="btn btn--ghost btn--sm">
              <Upload size={15} /> {t("dashboard.importEdsp")}
            </Link>
          )}
          <Link href="/dossiers" className="btn btn--primary btn--sm">
            <Plus size={15} /> {t("dashboard.newDossier")}
          </Link>
        </div>
      </div>

      {!grantOnly && (
        <KpiCards
          kpis={kpis}
          extra={
            <TechPayoutsCard
              data={techPayoutsQ.data}
              isLoading={techPayoutsQ.isLoading}
              t={t}
              urgentTotal={tr ? num(tr.payable.technicianCommission.releasedTotal) : 0}
            />
          }
        />
      )}

      <OperationBoard companyId={companyId} compact />

      <AgendaWeekCard companyId={companyId} />

      <div className="grid2">
        <section className="panel">
          <div className="panel__h">
            <h2>{t("dashboard.recentDossiers")}</h2>
            <Link href="/dossiers">{t("dashboard.viewAll")} →</Link>
          </div>
          <div className="toolbar">
            <button className="chip chip--active">{t("dashboard.chipAll")}</button>
            <button className="chip">{t("dashboard.chipToInvoice")}</button>
            <button className="chip">{t("dashboard.chipPending")}</button>
            <span className="spacer" />
            <Link href="/dossiers" className="chip">
              <SlidersHorizontal size={14} /> {t("dashboard.filters")}
            </Link>
          </div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t("dashboard.colRef")}</th>
                  <th>{t("dashboard.colDate")}</th>
                  <th>{t("dashboard.colVehicle")}</th>
                  <th>{t("dashboard.colTechnician")}</th>
                  <th>{t("dashboard.colGarage")}</th>
                  <th className="r">{t("dashboard.colGross")}</th>
                  <th className="r">{t("dashboard.colProfit")}</th>
                  <th>{t("dashboard.colStatus")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => {
                  const bruto = num(d.facturadoTotal);
                  const liquido = Math.max(0, bruto - num(d.technicianAmountTotal));
                  const b = statusBadge(d, t);
                  return (
                    <tr key={d.id} onClick={() => (window.location.href = `/dossiers/${d.id}`)}>
                      <td>
                        <span className="ref">{refOf(d)}</span>
                      </td>
                      <td className="muted">{dateOf(d, intl)}</td>
                      <td className="strong">
                        {d.vehicleMakeSnapshot ?? "—"}{" "}
                        <span className="muted">{d.vehicleModelSnapshot ?? ""}</span>
                      </td>
                      <td>{d.technicianName ?? "—"}</td>
                      <td className="muted">
                        {d.customerDisplayName ?? "—"}
                        {d.endCustomerName && (
                          <div style={{ fontSize: 11, opacity: 0.75 }}>↳ {d.endCustomerName}</div>
                        )}
                      </td>
                      <td className="r strong num">{money(bruto)} €</td>
                      <td className="r num">{money(liquido)} €</td>
                      <td>
                        <span className={`badge ${b.cls}`}>
                          <span className="led" />
                          {b.txt}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      style={{ textAlign: "center", padding: 24, color: "var(--text-muted)" }}
                    >
                      {dossiersQ.isLoading ? "…" : t("dashboard.emptyDossiers")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <div className="stack">
          <section className="panel" style={{ padding: 16 }}>
            <div className="panel__h" style={{ padding: "0 0 12px", margin: 0 }}>
              <h2>{t("dashboard.bankReconciliation")}</h2>
            </div>
            <div className="recon__row">
              <span className="recon__big num">
                {bankPct}
                <span style={{ fontSize: ".5em", color: "var(--text-muted)" }}>%</span>
              </span>
              <span className="recon__cap">
                {bankRec.toLocaleString(intl)} / {bankTotal.toLocaleString(intl)}{" "}
                {t("dashboard.reconciledSuf")}
              </span>
            </div>
            <div className="recon__bar">
              <div className="recon__fill" style={{ width: `${bankPct}%` }} />
            </div>
            <div className="recon__cap">
              <b style={{ color: "var(--warning-500)" }}>{bankUnrec}</b>{" "}
              {t("dashboard.awaitingReconciliationSuf")}
            </div>
          </section>

          <section className="panel">
            <div className="panel__h">
              <h2>{t("dashboard.todoTitle")}</h2>
              <span className="sub">
                {todos.length} {t("dashboard.itemsSuf")}
              </span>
            </div>
            <ul className="todo">
              {todos.length === 0 && (
                <li>
                  <span
                    className="todo__ic"
                    style={{ background: "var(--accent-soft)", color: "var(--accent)" }}
                  >
                    <FileCheck2 size={16} />
                  </span>
                  <span>
                    <span className="todo__t">{t("dashboard.allCaughtUp")}</span>
                    <span className="todo__s">{t("dashboard.nothingPending")}</span>
                  </span>
                </li>
              )}
              {todos.map((it, i) => {
                const Icon = it.icon;
                return (
                  <li
                    key={i}
                    onClick={() => setLocation(it.href)}
                    style={{ cursor: "pointer" }}
                    title={t("dashboard.open")}
                  >
                    <span className="todo__ic" style={{ background: it.bg, color: it.fg }}>
                      <Icon size={16} />
                    </span>
                    <span>
                      <span className="todo__t">{it.t}</span>
                      <span className="todo__s">{it.s}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="panel">
            <div className="panel__h">
              <h2>{t("dashboard.treasuryTitle")}</h2>
            </div>
            <div className="mini-stat">
              <span className="mini-stat__l">
                <Wallet size={15} /> {t("dashboard.receivableCustomers")}
              </span>
              <span className="mini-stat__v num">
                {tr ? money(tr.receivable.customer.openTotal) : "…"}
              </span>
            </div>
            <div className="mini-stat">
              <span className="mini-stat__l">
                <ArrowDownLeft size={15} /> {t("dashboard.overdue")}
              </span>
              <span className="mini-stat__v num" style={{ color: "var(--danger-500)" }}>
                {tr ? money(tr.receivable.customer.overdueTotal) : "…"}
              </span>
            </div>
            <div className="mini-stat">
              <span className="mini-stat__l">
                <ArrowUpRight size={15} /> {t("dashboard.payableTechnicians")}
              </span>
              <span className="mini-stat__v num">
                {tr ? money(tr.payable.technicianCommission.pendingTotal) : "…"}
              </span>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <ErpShell active="dashboard">
      <Painel />
    </ErpShell>
  );
}

/**
 * F-agenda-kpi (CEO 2026-09-19) — card da AGENDA no painel: RDVs de hoje e
 * da semana + próximos, com atalho para /agenda.
 */
function AgendaWeekCard({ companyId }: { companyId: number | null }) {
  const t = useT();
  const monday = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return d;
  })();
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 6);
  const weekQ = trpc.chantierAgenda.erpWeek.useQuery(
    { companyId: companyId ?? 0, from: iso(monday), to: iso(sunday) },
    { enabled: companyId != null },
  );
  const rows = weekQ.data?.rows ?? [];
  const todayIso = iso(new Date());
  const todayCount = rows.filter((r) => r.date === todayIso).length;
  const upcoming = rows.filter((r) => r.date >= todayIso).slice(0, 3);
  return (
    <section className="panel" style={{ padding: 14, marginBottom: 14 }}>
      <div className="panel__h" style={{ marginBottom: 6 }}>
        <h2>📅 {t("agendaPage.title")}</h2>
        <Link href="/agenda">{t("agendaPage.openAgenda")} →</Link>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 14, alignItems: "center" }}>
        <span style={{ fontSize: 22, fontWeight: 700 }}>
          {todayCount}
          <span style={{ fontSize: 12, fontWeight: 500, opacity: 0.7 }}>
            {" "}
            {t("agendaPage.kpiToday")}
          </span>
        </span>
        <span style={{ fontSize: 22, fontWeight: 700 }}>
          {rows.length}
          <span style={{ fontSize: 12, fontWeight: 500, opacity: 0.7 }}>
            {" "}
            {t("agendaPage.kpiWeek")}
          </span>
        </span>
        {(weekQ.data?.weekTotal ?? 0) > 0 && (
          <span
            title={t("agendaPage.weekTotalTitle")}
            style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 15 }}
          >
            {formatMoney(weekQ.data!.weekTotal, { decimals: 2 })} HT
          </span>
        )}
        <span style={{ flex: 1 }} />
        {upcoming.length === 0 && (
          <span style={{ fontSize: 12, opacity: 0.6 }}>{t("agendaPage.noRdvWeek")}</span>
        )}
        {upcoming.map((r) => (
          <span key={r.id} style={{ fontSize: 12, opacity: 0.85 }}>
            {r.date.slice(8, 10)}/{r.date.slice(5, 7)} {r.time}
            {r.plate ? ` · ${r.plate}` : ""}
            {r.customerName ? ` · ${r.customerName}` : r.chantierName ? ` · ${r.chantierName}` : ""}
          </span>
        ))}
      </div>
    </section>
  );
}
